import { memo, useEffect, useMemo, useRef, useState } from 'react';
import foreheadFace from './forehead-head-new.webp';
import foreheadHair from './forehead-wig-new.webp';

export const FIVEHEAD_ASSETS = [
  { name: 'CPT. FIVEHEAD face', src: foreheadFace },
  { name: 'CPT. FIVEHEAD wig', src: foreheadHair },
];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const readNumberParam = (name, fallback, min, max) => {
  const params = new URLSearchParams(window.location.search);
  const rawValue = params.get(name);
  const value = rawValue === null ? fallback : Number(rawValue);

  if (!Number.isFinite(value)) return fallback;
  return clamp(value, min, max);
};

const readColorParam = (names, fallback) => {
  const params = new URLSearchParams(window.location.search);
  const keys = Array.isArray(names) ? names : [names];
  const value = keys.map((name) => params.get(name)).find(Boolean);

  return value || fallback;
};

function useCompactViewport() {
  const [isCompact, setIsCompact] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia('(max-width: 640px)');
    const update = () => setIsCompact(query.matches);

    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return isCompact;
}

function useElementWidth(ref) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

export function isForeheadLabMode() {
  const params = new URLSearchParams(window.location.search);
  return params.get('foreheadLab') === '1' || window.location.pathname === '/forehead';
}

export const ForeheadMeter = memo(function ForeheadMeter({
  className = '',
  bandBottomWidth = 475,
  bandEdgeColor = '#6f2f08',
  bandEdgeWidth = 2.8,
  bandShadeColor = '#ffb773',
  bandShadeBottomWidth = 25,
  bandShadeTopWidth = 36,
  bandSkinColor = '#ffcc8c',
  bandHeightPct = 52.5,
  bandXOffset = 0.75,
  bandTopWidth = 476,
  bandYOffset = 1.75,
  blinkHeight = 2.25,
  blinkLeftX = 36.25,
  blinkRightX = 53.65,
  blinkWidth = 10.4,
  blinkY = 75.55,
  compactTuning,
  height,
  level,
  danger = true,
  topHeadYOffset = 0.25,
  wigOffsetX = -1.5,
  wigOffsetY = 2,
  wigScaleX = 0.62,
  wigScaleY = 0.9,
  won,
  pulse = 0,
  style: externalStyle = {},
}) {
  const avatarRef = useRef(null);
  const avatarWidth = useElementWidth(avatarRef);
  const shouldUseCompactTuning = compactTuning ?? !height;
  const useCompactTuning = useCompactViewport() && shouldUseCompactTuning;
  const clampedLevel = clamp(level, 0, 1);
  const visualLevel = clamp(clampedLevel + pulse * 0.06, 0, 1.06);
  const meterPercent = Math.round(clampedLevel * 100);
  const dangerLevel = danger ? 1 - clampedLevel : 0;
  const pulseLevel = clamp(pulse * 2.3, 0, 1);
  const effectiveBandBottomWidth = useCompactTuning ? bandBottomWidth - 12 : bandBottomWidth;
  const outlineScale = clamp((avatarWidth || 192) / 192, 0.34, 1.12);
  const effectiveBandEdgeWidth = bandEdgeWidth * outlineScale;
  const effectiveBandHeightPct = useCompactTuning ? bandHeightPct - 0.7 : bandHeightPct;
  const effectiveBandShadeBottomWidth = useCompactTuning ? bandShadeBottomWidth * 0.82 : bandShadeBottomWidth;
  const effectiveBandShadeTopWidth = useCompactTuning ? bandShadeTopWidth * 0.78 : bandShadeTopWidth;
  const effectiveBandTopWidth = useCompactTuning ? bandTopWidth - 14 : bandTopWidth;
  const effectiveBandYOffset = useCompactTuning ? bandYOffset - 0.35 : bandYOffset;
  const effectiveTopHeadYOffset = useCompactTuning ? topHeadYOffset - 0.15 : topHeadYOffset;
  const effectiveWigOffsetY = useCompactTuning ? wigOffsetY - 0.25 : wigOffsetY;

  const VIEWBOX_W = 1024;
  const svgBottomLeft = (VIEWBOX_W - effectiveBandBottomWidth) / 2;
  const svgBottomRight = VIEWBOX_W - svgBottomLeft;
  const svgTopLeft = (VIEWBOX_W - effectiveBandTopWidth) / 2;
  const svgTopRight = VIEWBOX_W - svgTopLeft;
  const bandPath = [
    `M ${svgBottomLeft} 100`,
    `C ${svgBottomLeft - 24} 72 ${svgTopLeft - 14} 28 ${svgTopLeft} 0`,
    `L ${svgTopRight} 0`,
    `C ${svgTopRight + 14} 28 ${svgBottomRight + 24} 72 ${svgBottomRight} 100`,
    'Z',
  ].join(' ');
  const leftShadePath = [
    `M ${svgBottomLeft} 100`,
    `C ${svgBottomLeft - 24} 72 ${svgTopLeft - 14} 28 ${svgTopLeft} 0`,
    `L ${svgTopLeft + effectiveBandShadeTopWidth} 0`,
    `C ${svgTopLeft + effectiveBandShadeTopWidth * 0.62} 28 ${svgBottomLeft + effectiveBandShadeBottomWidth * 0.78} 72 ${svgBottomLeft + effectiveBandShadeBottomWidth} 100`,
    'Z',
  ].join(' ');
  const rightShadePath = [
    `M ${svgBottomRight} 100`,
    `C ${svgBottomRight + 24} 72 ${svgTopRight + 14} 28 ${svgTopRight} 0`,
    `L ${svgTopRight - effectiveBandShadeTopWidth} 0`,
    `C ${svgTopRight - effectiveBandShadeTopWidth * 0.62} 28 ${svgBottomRight - effectiveBandShadeBottomWidth * 0.78} 72 ${svgBottomRight - effectiveBandShadeBottomWidth} 100`,
    'Z',
  ].join(' ');
  const wrinkleLines = [
    { threshold: 0.20, y: 84, inset: 154, bow: -1.5, width: 1.35 },
    { threshold: 0.30, y: 76, inset: 144, bow: 1.6, width: 1.45 },
    { threshold: 0.40, y: 68, inset: 136, bow: -2, width: 1.6 },
    { threshold: 0.50, y: 60, inset: 128, bow: 1.8, width: 1.55 },
    { threshold: 0.60, y: 51, inset: 120, bow: -1.5, width: 1.7 },
    { threshold: 0.70, y: 43, inset: 112, bow: 1.4, width: 1.55 },
    { threshold: 0.80, y: 34, inset: 106, bow: -1.3, width: 1.45 },
    { threshold: 0.90, y: 25, inset: 112, bow: 1.2, width: 1.25 },
  ].map((line) => {
    const left = svgBottomLeft + line.inset;
    const right = svgBottomRight - line.inset;
    const opacity = clamp((clampedLevel - line.threshold) * 5, 0, 0.38);

    return {
      ...line,
      d: `M ${left} ${line.y} Q 512 ${line.y + line.bow} ${right} ${line.y}`,
      opacity,
      width: line.width * outlineScale,
    };
  });
  const style = {
    '--forehead-level': visualLevel,
    '--forehead-band-max-height': `${effectiveBandHeightPct}%`,
    '--forehead-band-x-offset': `${bandXOffset}%`,
    '--forehead-band-y-offset': `${effectiveBandYOffset}%`,
    '--fivehead-danger': dangerLevel,
    '--fivehead-pulse': pulseLevel,
    '--forehead-blink-height': `${blinkHeight}%`,
    '--forehead-blink-left-x': `${blinkLeftX}%`,
    '--forehead-blink-right-x': `${blinkRightX}%`,
    '--forehead-blink-width': `${blinkWidth}%`,
    '--forehead-blink-y': `${blinkY}%`,
    '--forehead-eyelid-color': bandSkinColor,
    '--forehead-top-head-y-offset': `${effectiveTopHeadYOffset}%`,
    '--forehead-wig-offset-x': `${wigOffsetX}%`,
    '--forehead-wig-offset-y': `${effectiveWigOffsetY}%`,
    '--forehead-wig-scale-x': wigScaleX,
    '--forehead-wig-scale-y': wigScaleY,
  };

  if (height) {
    style['--forehead-component-height'] = `${height}px`;
  }

  return (
    <aside
      className={`forehead-score ${className} ${won ? 'is-maxed' : ''} ${danger && clampedLevel <= 0.28 ? 'is-danger' : ''} ${pulseLevel > 0 ? 'is-pulsing' : ''}`.trim()}
      style={{ ...style, ...externalStyle }}
      aria-label={`Forehead meter ${meterPercent} percent`}
      data-forehead-level={clampedLevel.toFixed(2)}
      data-testid="forehead-meter"
    >
      <div
        ref={avatarRef}
        className="forehead-avatar"
        role="img"
        aria-label={`Forehead avatar ${meterPercent} percent grown`}
        data-testid="forehead-avatar"
      >
        <div className="forehead-track" aria-label={`Fivehead progress ${meterPercent} percent`} data-testid="forehead-track">
          <div className="forehead-fill" />
          <div className="forehead-target">FULL</div>
        </div>
        <svg
          className="forehead-band"
          viewBox={`0 0 ${VIEWBOX_W} 100`}
          preserveAspectRatio="none"
          aria-hidden="true"
          data-testid="forehead-band"
        >
          <g>
            <path d={bandPath} fill={bandSkinColor} />
            <path d={leftShadePath} fill={bandShadeColor} />
            <path d={rightShadePath} fill={bandShadeColor} />
            {wrinkleLines.map((line) => (
              <path
                key={line.y}
                d={line.d}
                fill="none"
                opacity={line.opacity}
                stroke={bandEdgeColor}
                strokeLinecap="round"
                strokeWidth={line.width}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={bandPath} fill="none" stroke={bandEdgeColor} strokeWidth={effectiveBandEdgeWidth} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </g>
        </svg>
        <img className="forehead-dome" src={foreheadFace} alt="" aria-hidden="true" data-testid="forehead-dome" />
        <img className="forehead-face" src={foreheadFace} alt="" aria-hidden="true" data-testid="forehead-face" />
        <div className="forehead-blink" aria-hidden="true" data-testid="forehead-blink">
          <span className="forehead-eyelid forehead-eyelid--left" />
          <span className="forehead-eyelid forehead-eyelid--right" />
        </div>
        <img className="forehead-hair" src={foreheadHair} alt="" aria-hidden="true" data-testid="forehead-hair" />
      </div>
      <div className="forehead-label">CPT. FIVEHEAD</div>
    </aside>
  );
});

export function ForeheadMeterLab() {
  const [level, setLevel] = useState(readNumberParam('forehead', 0, 0, 1));
  const [height, setHeight] = useState(readNumberParam('meterHeight', 640, 280, 1100));
  const [bandBottomWidth, setBandBottomWidth] = useState(readNumberParam('bandBottomWidth', 475, 220, 560));
  const [bandEdgeColor, setBandEdgeColor] = useState(readColorParam('bandEdgeColor', '#6f2f08'));
  const [bandEdgeWidth, setBandEdgeWidth] = useState(readNumberParam('bandEdgeWidth', 2.8, 0, 12));
  const [bandShadeColor, setBandShadeColor] = useState(readColorParam(['bandShadeColor', 'bandLeftColor'], '#ffb773'));
  const [bandShadeBottomWidth, setBandShadeBottomWidth] = useState(readNumberParam('bandShadeBottomWidth', 25, 0, 220));
  const [bandShadeTopWidth, setBandShadeTopWidth] = useState(readNumberParam('bandShadeTopWidth', 36, 0, 220));
  const [bandSkinColor, setBandSkinColor] = useState(readColorParam(['bandSkinColor', 'bandMiddleColor'], '#ffcc8c'));
  const [bandHeightPct, setBandHeightPct] = useState(readNumberParam('bandHeightPct', 52.5, 18, 70));
  const [bandXOffset, setBandXOffset] = useState(readNumberParam('bandXOffset', 0.75, -30, 30));
  const [bandTopWidth, setBandTopWidth] = useState(readNumberParam('bandTopWidth', 476, 220, 560));
  const [bandYOffset, setBandYOffset] = useState(readNumberParam('bandYOffset', 1.75, -15, 15));
  const [topHeadYOffset, setTopHeadYOffset] = useState(readNumberParam('topHeadYOffset', 0.25, -15, 15));
  const [blinkHeight, setBlinkHeight] = useState(readNumberParam('blinkHeight', 2.25, 0.4, 8));
  const [blinkLeftX, setBlinkLeftX] = useState(readNumberParam('blinkLeftX', 36.25, 20, 55));
  const [blinkRightX, setBlinkRightX] = useState(readNumberParam('blinkRightX', 53.65, 45, 80));
  const [blinkWidth, setBlinkWidth] = useState(readNumberParam('blinkWidth', 10.4, 3, 18));
  const [blinkY, setBlinkY] = useState(readNumberParam('blinkY', 75.55, 68, 88));
  const [wigOffsetX, setWigOffsetX] = useState(readNumberParam('wigX', -1.5, -35, 35));
  const [wigOffsetY, setWigOffsetY] = useState(readNumberParam('wigY', 2, -35, 35));
  const [wigScaleX, setWigScaleX] = useState(readNumberParam('wigScaleX', 0.62, 0.35, 2));
  const [wigScaleY, setWigScaleY] = useState(readNumberParam('wigScaleY', 0.9, 0.35, 2.5));
  const percent = Math.round(level * 100);
  const updateLevel = (event) => setLevel(Number(event.target.value) / 100);
  const updateHeight = (event) => setHeight(Number(event.target.value));
  const updateBandBottomWidth = (event) => setBandBottomWidth(Number(event.target.value));
  const updateBandEdgeWidth = (event) => setBandEdgeWidth(Number(event.target.value));
  const updateBandHeightPct = (event) => setBandHeightPct(Number(event.target.value));
  const updateBandShadeBottomWidth = (event) => setBandShadeBottomWidth(Number(event.target.value));
  const updateBandShadeTopWidth = (event) => setBandShadeTopWidth(Number(event.target.value));
  const updateBandXOffset = (event) => setBandXOffset(Number(event.target.value));
  const updateBandTopWidth = (event) => setBandTopWidth(Number(event.target.value));
  const updateBandYOffset = (event) => setBandYOffset(Number(event.target.value));
  const updateTopHeadYOffset = (event) => setTopHeadYOffset(Number(event.target.value));
  const updateBlinkHeight = (event) => setBlinkHeight(Number(event.target.value));
  const updateBlinkLeftX = (event) => setBlinkLeftX(Number(event.target.value));
  const updateBlinkRightX = (event) => setBlinkRightX(Number(event.target.value));
  const updateBlinkWidth = (event) => setBlinkWidth(Number(event.target.value));
  const updateBlinkY = (event) => setBlinkY(Number(event.target.value));
  const updateWigOffsetX = (event) => setWigOffsetX(Number(event.target.value));
  const updateWigOffsetY = (event) => setWigOffsetY(Number(event.target.value));
  const updateWigScaleX = (event) => setWigScaleX(Number(event.target.value));
  const updateWigScaleY = (event) => setWigScaleY(Number(event.target.value));
  const labParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('foreheadLab', '1');
    params.set('forehead', level.toFixed(2));
    params.set('meterHeight', String(height));
    params.set('bandTopWidth', String(bandTopWidth));
    params.set('bandBottomWidth', String(bandBottomWidth));
    params.set('bandHeightPct', String(bandHeightPct));
    params.set('bandXOffset', String(bandXOffset));
    params.set('bandYOffset', String(bandYOffset));
    params.set('topHeadYOffset', String(topHeadYOffset));
    params.set('bandEdgeWidth', String(bandEdgeWidth));
    params.set('bandShadeTopWidth', String(bandShadeTopWidth));
    params.set('bandShadeBottomWidth', String(bandShadeBottomWidth));
    params.set('bandShadeColor', bandShadeColor);
    params.set('bandSkinColor', bandSkinColor);
    params.set('bandEdgeColor', bandEdgeColor);
    params.set('blinkY', String(blinkY));
    params.set('blinkLeftX', String(blinkLeftX));
    params.set('blinkRightX', String(blinkRightX));
    params.set('blinkWidth', String(blinkWidth));
    params.set('blinkHeight', String(blinkHeight));
    params.set('wigX', String(wigOffsetX));
    params.set('wigY', String(wigOffsetY));
    params.set('wigScaleX', String(wigScaleX));
    params.set('wigScaleY', String(wigScaleY));
    return params;
  }, [
    bandBottomWidth,
    bandEdgeColor,
    bandEdgeWidth,
    bandHeightPct,
    bandShadeColor,
    bandShadeBottomWidth,
    bandShadeTopWidth,
    bandSkinColor,
    bandTopWidth,
    bandXOffset,
    bandYOffset,
    blinkHeight,
    blinkLeftX,
    blinkRightX,
    blinkWidth,
    blinkY,
    height,
    level,
    topHeadYOffset,
    wigOffsetX,
    wigOffsetY,
    wigScaleX,
    wigScaleY,
  ]);
  const labQueryString = labParams.toString();

  useEffect(() => {
    window.history.replaceState(null, '', `${window.location.pathname}?${labQueryString}`);
  }, [labQueryString]);

  return (
    <main className="forehead-lab" aria-label="Forehead meter component lab">
      <div className="forehead-lab-stage" data-testid="forehead-lab-stage">
        <ForeheadMeter
          className="forehead-score--lab"
          height={height}
          level={level}
          wigOffsetX={wigOffsetX}
          wigOffsetY={wigOffsetY}
          wigScaleX={wigScaleX}
          wigScaleY={wigScaleY}
          bandBottomWidth={bandBottomWidth}
          bandEdgeColor={bandEdgeColor}
          bandEdgeWidth={bandEdgeWidth}
          bandShadeColor={bandShadeColor}
          bandShadeBottomWidth={bandShadeBottomWidth}
          bandShadeTopWidth={bandShadeTopWidth}
          bandSkinColor={bandSkinColor}
          bandHeightPct={bandHeightPct}
          bandXOffset={bandXOffset}
          bandTopWidth={bandTopWidth}
          bandYOffset={bandYOffset}
          blinkHeight={blinkHeight}
          blinkLeftX={blinkLeftX}
          blinkRightX={blinkRightX}
          blinkWidth={blinkWidth}
          blinkY={blinkY}
          topHeadYOffset={topHeadYOffset}
          won={level >= 1}
        />
      </div>

      <form className="forehead-lab-controls" aria-label="Forehead meter test controls">
        <label>
          <span>Forehead {percent}%</span>
          <input
            aria-label="Forehead level"
            type="range"
            min="0"
            max="100"
            value={percent}
            onChange={updateLevel}
            onInput={updateLevel}
          />
        </label>
        <label>
          <span>Height {height}px</span>
          <input
            aria-label="Forehead component height"
            type="range"
            min="280"
            max="1100"
            step="10"
            value={height}
            onChange={updateHeight}
            onInput={updateHeight}
          />
        </label>
        <label>
          <span>SVG height {bandHeightPct.toFixed(1)}%</span>
          <input aria-label="Forehead SVG height" type="range" min="18" max="70" step="0.1" value={bandHeightPct} onChange={updateBandHeightPct} onInput={updateBandHeightPct} />
        </label>
        <label>
          <span>Band top width {bandTopWidth}</span>
          <input aria-label="Forehead band top width" type="range" min="220" max="560" step="1" value={bandTopWidth} onChange={updateBandTopWidth} onInput={updateBandTopWidth} />
        </label>
        <label>
          <span>Band bottom width {bandBottomWidth}</span>
          <input aria-label="Forehead band bottom width" type="range" min="220" max="560" step="1" value={bandBottomWidth} onChange={updateBandBottomWidth} onInput={updateBandBottomWidth} />
        </label>
        <label>
          <span>SVG X {bandXOffset}%</span>
          <input aria-label="Forehead SVG horizontal position" type="range" min="-30" max="30" step="0.25" value={bandXOffset} onChange={updateBandXOffset} onInput={updateBandXOffset} />
        </label>
        <label>
          <span>SVG Y {bandYOffset}%</span>
          <input aria-label="Forehead SVG vertical position" type="range" min="-15" max="15" step="0.25" value={bandYOffset} onChange={updateBandYOffset} onInput={updateBandYOffset} />
        </label>
        <label>
          <span>Top head Y {topHeadYOffset}%</span>
          <input aria-label="Forehead top head vertical position" type="range" min="-15" max="15" step="0.25" value={topHeadYOffset} onChange={updateTopHeadYOffset} onInput={updateTopHeadYOffset} />
        </label>
        <label>
          <span>Edge width {bandEdgeWidth.toFixed(1)}</span>
          <input aria-label="Forehead band outline width" type="range" min="0" max="12" step="0.1" value={bandEdgeWidth} onChange={updateBandEdgeWidth} onInput={updateBandEdgeWidth} />
        </label>
        <label>
          <span>Shade top width {bandShadeTopWidth}</span>
          <input aria-label="Forehead side shade top width" type="range" min="0" max="220" step="1" value={bandShadeTopWidth} onChange={updateBandShadeTopWidth} onInput={updateBandShadeTopWidth} />
        </label>
        <label>
          <span>Shade bottom width {bandShadeBottomWidth}</span>
          <input aria-label="Forehead side shade bottom width" type="range" min="0" max="220" step="1" value={bandShadeBottomWidth} onChange={updateBandShadeBottomWidth} onInput={updateBandShadeBottomWidth} />
        </label>
        <label className="forehead-color-control">
          <span>Side shade</span>
          <input aria-label="Forehead band side shade colour" type="color" value={bandShadeColor} onChange={(event) => setBandShadeColor(event.target.value)} onInput={(event) => setBandShadeColor(event.target.value)} />
        </label>
        <label className="forehead-color-control">
          <span>Forehead skin</span>
          <input aria-label="Forehead band skin colour" type="color" value={bandSkinColor} onChange={(event) => setBandSkinColor(event.target.value)} onInput={(event) => setBandSkinColor(event.target.value)} />
        </label>
        <label className="forehead-color-control">
          <span>Outline</span>
          <input aria-label="Forehead band outline colour" type="color" value={bandEdgeColor} onChange={(event) => setBandEdgeColor(event.target.value)} onInput={(event) => setBandEdgeColor(event.target.value)} />
        </label>
        <label>
          <span>Blink Y {blinkY.toFixed(2)}%</span>
          <input aria-label="Forehead blink vertical position" type="range" min="68" max="88" step="0.05" value={blinkY} onChange={updateBlinkY} onInput={updateBlinkY} />
        </label>
        <label>
          <span>Blink left X {blinkLeftX.toFixed(2)}%</span>
          <input aria-label="Forehead blink left eye horizontal position" type="range" min="20" max="55" step="0.05" value={blinkLeftX} onChange={updateBlinkLeftX} onInput={updateBlinkLeftX} />
        </label>
        <label>
          <span>Blink right X {blinkRightX.toFixed(2)}%</span>
          <input aria-label="Forehead blink right eye horizontal position" type="range" min="45" max="80" step="0.05" value={blinkRightX} onChange={updateBlinkRightX} onInput={updateBlinkRightX} />
        </label>
        <label>
          <span>Blink width {blinkWidth.toFixed(2)}%</span>
          <input aria-label="Forehead blink width" type="range" min="3" max="18" step="0.05" value={blinkWidth} onChange={updateBlinkWidth} onInput={updateBlinkWidth} />
        </label>
        <label>
          <span>Blink height {blinkHeight.toFixed(2)}%</span>
          <input aria-label="Forehead blink height" type="range" min="0.4" max="8" step="0.05" value={blinkHeight} onChange={updateBlinkHeight} onInput={updateBlinkHeight} />
        </label>
        <label>
          <span>Wig X {wigOffsetX}%</span>
          <input
            aria-label="Wig horizontal position"
            type="range"
            min="-35"
            max="35"
            step="0.5"
            value={wigOffsetX}
            onChange={updateWigOffsetX}
            onInput={updateWigOffsetX}
          />
        </label>
        <label>
          <span>Wig Y {wigOffsetY}%</span>
          <input
            aria-label="Wig vertical position"
            type="range"
            min="-35"
            max="35"
            step="0.5"
            value={wigOffsetY}
            onChange={updateWigOffsetY}
            onInput={updateWigOffsetY}
          />
        </label>
        <label>
          <span>Wig scale X {wigScaleX.toFixed(2)}</span>
          <input
            aria-label="Wig horizontal scale"
            type="range"
            min="0.35"
            max="2"
            step="0.01"
            value={wigScaleX}
            onChange={updateWigScaleX}
            onInput={updateWigScaleX}
          />
        </label>
        <label>
          <span>Wig scale Y {wigScaleY.toFixed(2)}</span>
          <input
            aria-label="Wig vertical scale"
            type="range"
            min="0.35"
            max="2.5"
            step="0.01"
            value={wigScaleY}
            onChange={updateWigScaleY}
            onInput={updateWigScaleY}
          />
        </label>
        <div className="forehead-lab-buttons">
          {[0, 0.25, 0.5, 0.75, 1].map((preset) => (
            <button key={preset} type="button" onClick={() => setLevel(preset)}>
              {Math.round(preset * 100)}%
            </button>
          ))}
        </div>
        <textarea
          className="forehead-lab-output"
          aria-label="Current forehead lab URL parameters"
          readOnly
          value={`?${labQueryString}`}
          onFocus={(event) => event.target.select()}
        />
      </form>
    </main>
  );
}
